import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import { dashScrollbarSx } from "../shared/dashboardEnterprise";

const COLORS = {
  text: "#222222",
  muted: "#6b7280",
  soft: "#94a3b8",
  line: "#e2e9ef",
  card: "#ffffff",
  fleet: "#3b82f6",
  workshop: "#00c179",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const TONE_COLOR = {
  "bd-up": COLORS.workshop,
  "bd-down": COLORS.danger,
  "bd-warning": COLORS.warning,
  "bd-fleet": COLORS.fleet,
  "bd-muted": COLORS.muted,
};

const TABLE_SX = {
  minWidth: 760,
  "& .MuiTableCell-root": {
    fontFamily: "inherit",
    fontSize: 12,
    borderColor: COLORS.line,
    textAlign: "left",
    whiteSpace: "nowrap",
    py: "12px",
    px: "8px",
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    color: COLORS.muted,
    backgroundColor: COLORS.card,
  },
  "& .MuiTableSortLabel-root": {
    color: COLORS.muted,
    "&:hover": { color: COLORS.fleet },
    "&.Mui-active": { color: COLORS.fleet },
  },
  "& .MuiTableSortLabel-icon": {
    color: `${COLORS.soft} !important`,
  },
  "& .Mui-active .MuiTableSortLabel-icon": {
    color: `${COLORS.fleet} !important`,
  },
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

const SEARCH_SX = {
  width: 260,
  maxWidth: "100%",
  flexShrink: 0,
  "& .MuiInputBase-root": {
    fontFamily: "inherit",
    fontSize: 12,
    color: COLORS.text,
    backgroundColor: COLORS.card,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.line,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.soft,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.fleet,
  },
};

const toneOf = (column, row) => {
  if (typeof column.tone === "function") return column.tone(row) || "";
  if (column.tone) return column.tone;
  if (typeof column.className === "function") return column.className(row) || "";
  return column.className || "";
};

const cellIsBold = (column, row) => {
  if (typeof column.bold === "function") return column.bold(row);
  return Boolean(column.bold);
};

const searchTextOf = (column, row) => {
  const value = column.searchText ? column.searchText(row) : row[column.key];
  return String(value ?? "").toLowerCase();
};

const sortValueOf = (column, row) => {
  if (column.sortValue) return column.sortValue(row);
  return row[column.key];
};

const compareRows = (leftRow, rightRow, sort, columns) => {
  const column = columns.find((item) => item.key === sort.key);
  const left = column ? sortValueOf(column, leftRow) : leftRow[sort.key];
  const right = column ? sortValueOf(column, rightRow) : rightRow[sort.key];
  const direction = sort.direction === "asc" ? 1 : -1;
  const leftEmpty = left == null || left === "";
  const rightEmpty = right == null || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  if (typeof left === "string" || typeof right === "string") {
    return String(left).localeCompare(String(right), undefined, { sensitivity: "base" }) * direction;
  }
  return (Number(left) - Number(right)) * direction;
};

const DashboardTable = ({
  columns,
  rows,
  loading,
  loadingLabel = "…",
  emptyLabel = "—",
  maxHeight,
  defaultSort,
  searchPlaceholder = "Search",
  header,
  headerAside,
  rowKey,
}) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(defaultSort || { key: columns[0]?.key, direction: "desc" });
  const scrollable = maxHeight != null;
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? rows.filter((row) => columns.some((column) => searchTextOf(column, row).includes(needle)))
      : rows;
    if (!sort?.key) return matched;
    return [...matched].sort((leftRow, rightRow) => compareRows(leftRow, rightRow, sort, columns));
  }, [columns, query, rows, sort]);

  const sortBy = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {header}
          {headerAside ? <Box sx={{ mt: 0.5 }}>{headerAside}</Box> : null}
        </Box>
        <TextField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          size="small"
          sx={SEARCH_SX}
        />
      </Box>
      <TableContainer
        sx={{
          maxHeight: scrollable ? maxHeight : undefined,
          overflow: "auto",
          ...dashScrollbarSx,
        }}
      >
        <Table stickyHeader={scrollable} size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const sortable = column.sortable !== false;
                const active = sort?.key === column.key;
                return (
                  <TableCell
                    key={column.key}
                    align="left"
                    sortDirection={active ? sort.direction : false}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.direction : "desc"}
                        onClick={() => sortBy(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={columns.length}>{loadingLabel}</TableCell>
              </TableRow>
            )}
            {!loading && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>{rows.length === 0 ? emptyLabel : "No matches."}</TableCell>
              </TableRow>
            )}
            {!loading && visibleRows.map((row, index) => (
              <TableRow key={rowKey ? rowKey(row, index) : index}>
                {columns.map((column) => {
                  const tone = toneOf(column, row);
                  return (
                    <TableCell
                      key={column.key}
                      sx={{
                        fontWeight: cellIsBold(column, row) ? 700 : undefined,
                        color: TONE_COLOR[tone] || COLORS.text,
                        "& .bd-money": { color: "inherit" },
                      }}
                    >
                      {column.render ? column.render(row) : row[column.key]}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DashboardTable;
